import { Injectable } from '@angular/core';
import FileSaver from 'file-saver';
import { WorkBook } from 'xlsx';
import * as XLSX from 'xlsx-js-style';


export interface ExcelReport{
  data: any[];
  sheetName: string;
  skipHeader?: boolean;
  formatTables?: boolean;
}
@Injectable({
  providedIn: 'root'
})
export class ReportService {

  constructor() { }

  exportExcel(reportData: ExcelReport[], fileName: string) {
    let workbook:WorkBook = { Sheets: {}, SheetNames: [] };
    reportData.forEach((report) => {
      let worksheet;
      if (report.skipHeader) {
        // Para datos como arrays, usar aoa_to_sheet sin encabezados automáticos
        worksheet = XLSX.utils.aoa_to_sheet(report.data);
      } else {
        // Para datos como objetos, usar json_to_sheet (comportamiento original)
        worksheet = XLSX.utils.json_to_sheet(report.data);
      }

      // Aplicar formato de tablas si está habilitado
      if (report.formatTables && report.skipHeader) {
        this.applyTableFormatting(worksheet, report.data);
      }

      workbook.Sheets[report.sheetName] = worksheet;
      workbook.SheetNames.push(report.sheetName);
    });
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    this.saveAsExcelFile(excelBuffer, fileName);
  }

  private applyTableFormatting(worksheet: any, data: any[]): void {
    // Aplicar estilos a las celdas
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });

        if (!worksheet[cellAddress]) continue;

        // Formato para segmento de información (filas 1-4)
        if (row >= 0 && row <= 3) {
          // Estilo diferenciado para etiquetas (columna A) y valores (columna B)
          if (col === 0) {
            worksheet[cellAddress].s = {
              font: { bold: true, sz: 11, color: { rgb: "FFFFFF" }, name: "Calibri" },
              fill: { fgColor: { rgb: "2D7A3E" } },
              border: {
                top: { style: "thin", color: { rgb: "FFFFFF" } },
                bottom: { style: "thin", color: { rgb: "FFFFFF" } },
                left: { style: "thin", color: { rgb: "FFFFFF" } },
                right: { style: "thin", color: { rgb: "FFFFFF" } }
              },
              alignment: { horizontal: "left", vertical: "center", wrapText: false }
            };
          } else {
            worksheet[cellAddress].s = {
              font: { bold: false, sz: 11, color: { rgb: "000000" }, name: "Calibri" },
              fill: { fgColor: { rgb: "D4EDDA" } },
              border: {
                top: { style: "thin", color: { rgb: "FFFFFF" } },
                bottom: { style: "thin", color: { rgb: "FFFFFF" } },
                left: { style: "thin", color: { rgb: "FFFFFF" } },
                right: { style: "thin", color: { rgb: "FFFFFF" } }
              },
              alignment: { horizontal: "left", vertical: "center", wrapText: false }
            };
          }
        }
        // Formato para encabezados de columnas (fila 9, índice 8)
        else if (row === 8) {
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 11, color: { rgb: "FFFFFF" }, name: "Calibri" },
            fill: { fgColor: { rgb: "28A745" } },
            border: {
              top: { style: "medium", color: { rgb: "28A745" } },
              bottom: { style: "medium", color: { rgb: "28A745" } },
              left: { style: "thin", color: { rgb: "FFFFFF" } },
              right: { style: "thin", color: { rgb: "FFFFFF" } }
            },
            alignment: { horizontal: "center", vertical: "center", wrapText: true }
          };
        }
        // Formato para datos de jugadores (fila 10 en adelante)
        else if (row >= 9) {
          worksheet[cellAddress].s = {
            font: { sz: 10, color: { rgb: "000000" }, name: "Calibri" },
            fill: { fgColor: { rgb: row % 2 === 1 ? "F2F2F2" : "FFFFFF" } },
            border: {
              top: { style: "hair", color: { rgb: "D9D9D9" } },
              bottom: { style: "hair", color: { rgb: "D9D9D9" } },
              left: { style: "hair", color: { rgb: "D9D9D9" } },
              right: { style: "hair", color: { rgb: "D9D9D9" } }
            },
            alignment: { horizontal: "left", vertical: "center", wrapText: false }
          };
        }
      }
    }

    // Ajustar altura de filas
    worksheet['!rows'] = [];
    for (let row = range.s.r; row <= range.e.r; row++) {
      if (row >= 0 && row <= 3) {
        worksheet['!rows'][row] = { hpt: 20 }; // Altura para info header
      } else if (row === 8) {
        worksheet['!rows'][row] = { hpt: 30 }; // Altura para encabezados de tabla
      } else if (row >= 9) {
        worksheet['!rows'][row] = { hpt: 18 }; // Altura para datos
      }
    }

    // Ajustar ancho de columnas de forma automática o manual
    const colWidths = [
      { wch: 18 }, // Nombre / Rol
      { wch: 18 }, // Apellido / Número de Juego
      { wch: 18 }, // Tipo de Documento / Nombre
      { wch: 18 }, // Número de Documento / Apellido
      { wch: 18 }, // Género / Tipo Doc
      { wch: 15 }, // Fecha de Nacimiento / Num Doc
      { wch: 25 }, // Club / Género
      { wch: 25 }, // Equipo / Fecha Nac
      { wch: 12 }, // Número de Juego
      { wch: 15 }, // Rol
      { wch: 15 }  // Nivel
    ];

    worksheet['!cols'] = colWidths;
  }

 private saveAsExcelFile(buffer: any, fileName: string): void {
    let EXCEL_TYPE =
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    let EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(data, fileName + EXCEL_EXTENSION);
  }
}
